import {
  CLAIM_STATUS_LABELS,
  CLIENT_USER_STATUS,
  CLIENT_USER_TYPE,
} from "./clientPortalConstants";

function asArray(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.items)) return value.items;
  return [];
}

function cleanText(value) {
  return typeof value === "string" ? value.trim() : "";
}

function isValidOrderEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanText(value));
}

function finiteNumber(value) {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value !== "string") return null;
  const text = value.trim();
  if (
    !text ||
    !/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/.test(text)
  ) {
    return null;
  }
  const number = Number(text);
  return Number.isFinite(number) ? number : null;
}

function isPlainObject(value) {
  return Boolean(
    value &&
      typeof value === "object" &&
      !Array.isArray(value)
  );
}

function referenceId(value) {
  const candidate = isPlainObject(value)
    ? value.iref ?? value.id
    : value;
  if (typeof candidate === "string") {
    return candidate.trim() || null;
  }
  return typeof candidate === "number" && Number.isFinite(candidate)
    ? candidate
    : null;
}

function displayNameFromUser(user) {
  const person = user?.person || {};
  const personName = [person.firstName, person.lastName].map(cleanText).filter(Boolean).join(" ");
  const userName = [user?.first_name, user?.last_name].map(cleanText).filter(Boolean).join(" ");
  return personName || userName || cleanText(user?.username) || "";
}

function selectTranslation(rows, languageIref) {
  const translations = asArray(rows);
  const requested = String(languageIref || "");
  if (!translations.length || !requested) return null;
  return translations.find((row) => {
    const language = row?.language;
    const id = language?.iref ?? language?.id ?? language;
    return String(id) === requested;
  }) || null;
}

function imagePath(raw) {
  return cleanText(raw?.image_url || raw?.image || raw?.imageUrl);
}

export function normalizeWhoAmI(raw) {
  const profile = raw?.profile;
  const userType = finiteNumber(profile?.user_type);
  if (!profile || userType !== CLIENT_USER_TYPE) return null;

  return {
    userType,
    displayName: displayNameFromUser(profile),
  };
}

export function normalizeClientProfile(raw) {
  const clients = asArray(raw?.clients ?? raw);
  if (clients.length !== 1) return null;

  const client = clients[0];
  if (!isPlainObject(client) || !isPlainObject(client.user)) return null;
  const relationId = finiteNumber(client.iref);
  const credit = finiteNumber(client?.credit);
  if (relationId == null || relationId <= 0 || credit == null) return null;
  const user = client.user;
  const person = isPlainObject(user.person) ? user.person : {};
  const address = isPlainObject(person.address) ? person.address : {};
  const cityRaw = isPlainObject(address.city) ? address.city : {};
  const countyRaw = isPlainObject(cityRaw.county) ? cityRaw.county : {};
  const firstName =
    cleanText(person.firstName) || cleanText(user.first_name);
  const lastName =
    cleanText(person.lastName) || cleanText(user.last_name);
  const email = cleanText(person.email);
  const cityId = referenceId(cityRaw);
  const countyId = referenceId(countyRaw);
  return {
    id: relationId,
    displayName: displayNameFromUser(user),
    firstName,
    lastName,
    email,
    phone: cleanText(person.phone),
    languageId: referenceId(user.language),
    city: cityId
      ? {
          id: cityId,
          name: cleanText(cityRaw.name),
          label: cleanText(cityRaw.name),
        }
      : null,
    county: countyId
      ? {
          id: countyId,
          name: cleanText(countyRaw.name),
          label: cleanText(countyRaw.name),
        }
      : null,
    preferences: {
      marketing: Boolean(client.agreeementMarketing),
      email: Boolean(client.commEmail),
      phone: Boolean(client.commPhone),
      sms: Boolean(client.commSMS),
      promotions: Boolean(client.commSubjPromo),
      news: Boolean(client.commSubjNews),
      orderNotifications: Boolean(client.notifOrders),
      promotionNotifications: Boolean(client.notifPromo),
      newsNotifications: Boolean(client.notifNews),
      rewardNotifications: Boolean(client.notifRewards),
    },
    credit,
    hasOrderEmail: isValidOrderEmail(email),
    emailValidationPending:
      finiteNumber(user.user_status) === CLIENT_USER_STATUS.pending,
  };
}

function normalizeServerDate(value) {
  const text = cleanText(value);
  const match = text.match(
    /^(\d{2})-(\d{2})-(\d{4})(?:\s+(\d{2}):(\d{2}):(\d{2}))?$/
  );
  if (!match) return text || null;
  const [, day, month, year, hour = "00", minute = "00", second = "00"] =
    match;
  return `${year}-${month}-${day}T${hour}:${minute}:${second}`;
}

function orderItemCount(order) {
  const keys = [
    "orderProductEntry_order",
    "orderComboEntry_order",
    "orderProductPromotionEntry_order",
    "orderComboPromotionEntry_order",
    "orderProductCompanyEntry_order",
    "orderComboCompanyEntry_order",
  ];
  return keys.reduce(
    (total, key) =>
      total +
      asArray(order?.[key]).reduce((subtotal, row) => {
        const quantity = finiteNumber(row?.quantity);
        return subtotal + (quantity != null && quantity > 0 ? quantity : 1);
      }, 0),
    0
  );
}

export function normalizeClientOrders(raw) {
  const currencyLabels = {
    0: "RON",
    1: "EUR",
    2: "USD",
  };

  return asArray(raw?.orders ?? raw)
    .map((order) => {
      if (!isPlainObject(order)) return null;
      const id = referenceId(order.iref);
      const status = finiteNumber(order.status);
      const total = finiteNumber(order.totalAmount);
      if (id == null || status == null || total == null) return null;
      const currencyValue = finiteNumber(order.currency);
      const transactions = asArray(order.transaction_order);
      const latestTransaction = transactions[transactions.length - 1];

      return {
        id,
        code: cleanText(order.code) || String(id),
        status,
        total,
        currency:
          currencyLabels[currencyValue] || cleanText(order.currency) || "RON",
        createdAt: normalizeServerDate(order.createdOn),
        scheduledAt: normalizeServerDate(order.scheduledOn),
        deliveryType: finiteNumber(order.deliveryType),
        locationId: referenceId(order.location),
        itemCount: orderItemCount(order),
        paymentStatus: finiteNumber(latestTransaction?.status),
      };
    })
    .filter(Boolean)
    .slice(0, 5);
}

export function normalizeClientQR(raw) {
  const qr = raw?.clientQR;
  const value = cleanText(qr?.qrCode);
  if (!value) return null;
  return {
    value,
    expiresAt: qr?.qrExpireOn || null,
  };
}

function normalizePromotion(raw, languageIref, source = "promotion") {
  const translation = selectTranslation(raw?.promot_promotion, languageIref);
  const claims = asArray(raw?.pc_promotion);
  const id = finiteNumber(raw?.iref);
  if (id == null || id <= 0) return null;

  const type = finiteNumber(raw?.type);
  const value = finiteNumber(raw?.value);
  return {
    id,
    source,
    name: cleanText(translation?.name) || cleanText(raw?.name),
    description: cleanText(translation?.description) || cleanText(raw?.description),
    image: imagePath(raw),
    validFrom: raw?.dateFrom || null,
    validTo: raw?.dateTo || null,
    type,
    value,
    frequency: finiteNumber(raw?.frequency),
    ageFrom: finiteNumber(raw?.ageFrom),
    ageTo: finiteNumber(raw?.ageTo),
    onBirthday:
      raw?.onBirthday === true ||
      raw?.onBirthday === 1 ||
      raw?.onBirthday === "true",
    productId: referenceId(raw?.product),
    comboId: referenceId(raw?.combo),
    voucherCode: cleanText(raw?.vCode),
    claimed: claims.length > 0,
    claimable: claims.length === 0,
  };
}

export function normalizePromotions(raw, languageIref = "") {
  const promotions = asArray(raw?.promotions).map((item) =>
    normalizePromotion(item, languageIref, "promotion")
  );
  const vouchers = asArray(raw?.vouchers).map((item) =>
    normalizePromotion(item, languageIref, "voucher")
  );
  return [...promotions, ...vouchers].filter(Boolean);
}

export function claimStatusLabel(status) {
  const number = finiteNumber(status);
  return CLAIM_STATUS_LABELS[number] || "Status unavailable";
}

export function normalizeClaims(raw, languageIref = "") {
  const promotionRows = [
    ...asArray(raw?.promotions).map((promotion) => ({
      promotion,
      source: "promotion",
    })),
    ...asArray(raw?.vouchers).map((promotion) => ({
      promotion,
      source: "voucher",
    })),
  ];

  return promotionRows.flatMap(({ promotion, source }) => {
    const normalizedPromotion = normalizePromotion(
      promotion,
      languageIref,
      source
    );
    if (!normalizedPromotion) return [];

    return asArray(promotion?.pc_promotion).map((claim) => {
      const id = finiteNumber(claim?.iref);
      if (id == null || id <= 0) return null;
      const status = finiteNumber(claim?.status);
      return {
        id,
        promotionId: normalizedPromotion.id,
        source,
        name: normalizedPromotion.name,
        description: normalizedPromotion.description,
        image: normalizedPromotion.image,
        validFrom: normalizedPromotion.validFrom,
        validTo: normalizedPromotion.validTo,
        claimedOn: claim?.claimedOn || null,
        consumedOn: claim?.consumedOn || null,
        status,
        statusLabel: claimStatusLabel(status),
        selectable: status === 1 && !claim?.consumedOn,
        productId: normalizedPromotion.productId,
        comboId: normalizedPromotion.comboId,
        type: normalizedPromotion.type,
        value: normalizedPromotion.value,
        frequency: normalizedPromotion.frequency,
        ageFrom: normalizedPromotion.ageFrom,
        ageTo: normalizedPromotion.ageTo,
        onBirthday: normalizedPromotion.onBirthday,
      };
    }).filter(Boolean);
  });
}

export function normalizeDirectBenefits(raw) {
  return asArray(raw?.directPromotions ?? raw).map((item) => {
    const id = finiteNumber(item?.iref);
    if (id == null || id <= 0) return null;
    const status = finiteNumber(item?.status);
    return {
      id,
      source: "direct",
      name: cleanText(item?.name),
      description: cleanText(item?.description),
      image: imagePath(item),
      validFrom: item?.dateFrom || null,
      validTo: item?.dateTo || null,
      claimedOn: item?.claimedOn || null,
      consumedOn: item?.consumedOn || null,
      status,
      statusLabel: claimStatusLabel(status),
      productId: referenceId(item?.product),
      comboId: referenceId(item?.combo),
      claimed: false,
      claimable: false,
    };
  }).filter(Boolean);
}

export function normalizeNews(raw, languageIref = "") {
  return asArray(raw?.news ?? raw).map((item) => {
    const id = finiteNumber(item?.iref);
    if (id == null || id <= 0) return null;
    const translation = selectTranslation(item?.newst_news, languageIref);
    return {
      id,
      title: cleanText(translation?.name) || cleanText(item?.name),
      summary: cleanText(translation?.description) || cleanText(item?.description),
      details: cleanText(translation?.details) || cleanText(item?.details),
      detailsLanguage: finiteNumber(item?.detailsLanguage),
      image: imagePath(item),
      validFrom: item?.dateFrom || null,
      validTo: item?.dateTo || null,
    };
  }).filter(Boolean);
}

export function mergeClientOffers(
  promotions = [],
  claims = [],
  directBenefits = []
) {
  const promotionById = new Map();

  asArray(promotions).forEach((promotion) => {
    if (promotion?.id == null) return;
    promotionById.set(String(promotion.id), { ...promotion });
  });

  asArray(claims).forEach((claim) => {
    if (claim?.promotionId == null) return;
    const key = String(claim.promotionId);
    const current = promotionById.get(key);
    if (current) {
      promotionById.set(key, {
        ...current,
        claimed: true,
        claimable: false,
        claimId: claim.id,
        claimedOn: claim.claimedOn || current.claimedOn || null,
      });
      return;
    }

    promotionById.set(key, {
      id: claim.promotionId,
      source: claim.source === "voucher" ? "voucher" : "promotion",
      name: claim.name,
      description: claim.description,
      image: claim.image,
      validFrom: claim.validFrom || null,
      validTo: claim.validTo || null,
      type: claim.type,
      value: claim.value,
      frequency: claim.frequency,
      ageFrom: claim.ageFrom,
      ageTo: claim.ageTo,
      onBirthday: claim.onBirthday,
      productId: claim.productId,
      comboId: claim.comboId,
      claimed: true,
      claimable: false,
      claimId: claim.id,
      claimedOn: claim.claimedOn || null,
    });
  });

  return [
    ...promotionById.values(),
    ...asArray(directBenefits).map((benefit) => ({
      ...benefit,
      source: "direct",
      claimed: false,
      claimable: false,
    })),
  ];
}

export function formatClientCredit(
  value,
  locale = "ro-RO",
  labels = {}
) {
  const number = finiteNumber(value);
  const english = String(locale).toLowerCase().startsWith("en");
  if (number == null) {
    return labels.unavailable || (english ? "Unavailable" : "Indisponibil");
  }
  const formatted = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number);
  const singular = labels.singular || (english ? "point" : "punct");
  const plural = labels.plural || (english ? "points" : "puncte");
  return `${formatted} ${Math.abs(number) === 1 ? singular : plural}`;
}

export function formatClientPortalDate(value, locale = "ro-RO") {
  if (!value) return "";
  const text = cleanText(value);
  const backendDate = text.match(
    /^(\d{2})-(\d{2})-(\d{4})[ T](\d{2}):(\d{2}):(\d{2})$/
  );
  let parsed;
  if (backendDate) {
    const [, day, month, year, hour, minute, second] = backendDate.map(Number);
    parsed = new Date(year, month - 1, day, hour, minute, second);
    if (
      parsed.getFullYear() !== year ||
      parsed.getMonth() !== month - 1 ||
      parsed.getDate() !== day ||
      parsed.getHours() !== hour ||
      parsed.getMinutes() !== minute ||
      parsed.getSeconds() !== second
    ) {
      return text;
    }
  } else {
    parsed = new Date(text);
  }
  if (!Number.isNaN(parsed.getTime())) {
    return new Intl.DateTimeFormat(locale, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(parsed);
  }
  return text;
}
